import React from 'react';
import { Card, CardContent } from '../../../components/UI/Card';
import type { PhoneHealthSummary } from '../../../services/phoneNumberHealthService';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  FireIcon,
  SignalIcon,
  ChatBubbleBottomCenterTextIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { formatPercentRate } from './healthDashboardUtils';
import UtilizationBar from './UtilizationBar';

const pct = (n: number, total: number) => (total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%');

interface Props {
  summary: PhoneHealthSummary | null;
  loading?: boolean;
}

const HealthSummaryCards: React.FC<Props> = ({ summary, loading }) => {
  if (loading || !summary) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="animate-pulse h-24"><CardContent>{null}</CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  const poolCards = [
    { label: 'Total', value: summary.total, sub: 'Active numbers', icon: SignalIcon, color: '' },
    { label: 'Healthy', value: summary.healthy, sub: pct(summary.healthy, summary.total), icon: ShieldCheckIcon, color: 'text-green-600 dark:text-green-400' },
    { label: 'Warning', value: summary.warning, sub: pct(summary.warning, summary.total), icon: ExclamationTriangleIcon, color: 'text-amber-600 dark:text-amber-400' },
    { label: 'Replace', value: summary.replace, sub: pct(summary.replace, summary.total), icon: ArrowPathIcon, color: 'text-red-600 dark:text-red-400' },
    { label: 'Warm-Up', value: summary.warmUp, sub: pct(summary.warmUp, summary.total), icon: FireIcon, color: 'text-blue-600 dark:text-blue-400' },
  ];

  const metricCards = [
    {
      label: 'Pool Utilization',
      value: formatPercentRate(summary.poolUtilizationPercent ?? 0),
      sub: `${summary.poolUsedToday ?? 0} / ${summary.poolDailyCapacity ?? 0} calls · ${summary.periodLabel ?? 'Today'}`,
      icon: ChartBarIcon,
      color:
        (summary.poolUtilizationPercent ?? 0) >= 0.8
          ? 'text-amber-600 dark:text-amber-400'
          : '',
      utilization: summary.poolUtilizationPercent ?? 0,
      isPool: true,
    },
    { label: 'Connected Rate', value: formatPercentRate(summary.avgConnectRate), sub: `${summary.periodLabel ?? 'Today'} (disposition)`, icon: SignalIcon, color: '' },
    { label: 'Delivery Rate', value: formatPercentRate(summary.avgDeliveryRate), sub: `${summary.periodLabel ?? 'Today'} SMS`, icon: ChatBubbleBottomCenterTextIcon, color: summary.avgDeliveryRate > 0 ? '' : 'text-muted-foreground' },
    { label: 'Carrier Spam', value: summary.carrierSpamIssues, sub: 'Flagged numbers', icon: ExclamationCircleIcon, color: summary.carrierSpamIssues > 0 ? 'text-amber-600 dark:text-amber-400' : '' },
  ];

  const segments = [
    { key: 'healthy', count: summary.healthy, color: 'bg-green-500' },
    { key: 'warning', count: summary.warning, color: 'bg-amber-500' },
    { key: 'replace', count: summary.replace, color: 'bg-red-500' },
    { key: 'warmUp', count: summary.warmUp, color: 'bg-blue-500' },
  ].filter((s) => s.count > 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {poolCards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="hover:shadow-sm transition-shadow border-border/80">
              <CardContent className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon className={`h-3.5 w-3.5 ${c.color || 'text-muted-foreground'}`} />
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">{c.label}</p>
                </div>
                <p className={`text-xl font-bold tabular-nums ${c.color}`}>{c.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{c.sub}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {metricCards.map((c) => {
          const Icon = c.icon;
          const poolCard = 'isPool' in c && c.isPool;
          return (
            <Card key={c.label} className="hover:shadow-sm transition-shadow border-border/80">
              <CardContent className="p-3 flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center shrink-0">
                    <Icon className={`h-5 w-5 ${c.color || 'text-muted-foreground'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{c.label}</p>
                    <p className={`text-lg font-bold tabular-nums ${c.color}`}>{c.value}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{c.sub}</p>
                  </div>
                </div>
                {poolCard && (
                  <UtilizationBar
                    used={summary.poolUsedToday ?? 0}
                    capacity={summary.poolDailyCapacity ?? 0}
                    percent={summary.poolUtilizationPercent}
                    showCounts={false}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {summary.total > 0 && segments.length > 0 && (
        <div className="rounded-xl border border-border/80 bg-card/50 px-4 py-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pool health mix</p>
            <p className="text-xs text-muted-foreground tabular-nums">{summary.total} numbers</p>
          </div>
          <div className="flex h-2.5 rounded-full overflow-hidden bg-muted/50">
            {segments.map((s) => (
              <div
                key={s.key}
                className={`${s.color} transition-all`}
                style={{ width: `${(s.count / summary.total) * 100}%` }}
                title={`${s.key}: ${s.count}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-muted-foreground">
            {segments.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${s.color}`} />
                {s.key.replace('warmUp', 'warm-up')}: {s.count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthSummaryCards;
